import { Button, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { CommitmentCancelCorrectPageProps } from "features/commitments/cancel-correct/CommitmentCancelCorrectPageProps";
import {
  getCommitmentInformationRetrieveQueryKey,
  useCommitmentInformationRetrieveSuspense,
} from "gen/endpoints/commitment-information/commitment-information";
import {
  useFundFamilyEventDelete,
  useFundFamilyEventRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import {
  getSecurityOwnershipCancelCorrectListQueryKey,
  useSecurityOwnershipCancelCorrectListSuspense,
} from "gen/endpoints/security-ownership/security-ownership";
import { SecurityOwnershipCancelCorrectReadData } from "gen/model/securityOwnershipCancelCorrectReadData";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import { FullScreenLoadingState } from "shared/components/empty/FullScreenLoadingState";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardProvider,
  PageWizardRoutes,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CommitmentCancelCorrectPage: React.FC = () => {
  const { fundFamilyId, commitmentId, fundFamilyEventId } =
    useRequiredStringParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: cancelCorrectData } =
    useSecurityOwnershipCancelCorrectListSuspense(fundFamilyId, commitmentId);
  const { data: commitmentInformation } =
    useCommitmentInformationRetrieveSuspense(fundFamilyId, commitmentId);

  const { t } = useTranslation(["commitment", "common"]);
  const draftCancelCorrectEvent:
    | SecurityOwnershipCancelCorrectReadData
    | undefined = cancelCorrectData.find(
    (event: SecurityOwnershipCancelCorrectReadData) =>
      event.event_id === fundFamilyEventId,
  );

  const pageProps: CommitmentCancelCorrectPageProps = {
    fundFamilyId,
    commitmentId,
    fundFamilyEventId,
    cancelCorrectData: draftCancelCorrectEvent,
  };

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      query: {
        refetchInterval: 1000,
      },
    },
  );

  const afterDelete = async () => {
    await invalidateQueryKeys(queryClient, [
      getSecurityOwnershipCancelCorrectListQueryKey(fundFamilyId, commitmentId),
      getCommitmentInformationRetrieveQueryKey(fundFamilyId, commitmentId),
    ]);
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .commitments({})
        .commitment({ commitmentId })
        .profile({}).$,
    );
  };

  return (
    <PageWizardProvider>
      <PageContent>
        <PageBody>
          {fundFamilyEventData?.event_workflow_status.is_running ? (
            <FullScreenLoadingState
              message={fundFamilyEventData.event_workflow_status.message}
            />
          ) : (
            <>
              <CommitmentCancelCorrectHeader afterDelete={afterDelete} />
              {fundFamilyEventData &&
                !!fundFamilyEventData.out_of_date_reason && (
                  <OutOfDateAlert
                    fundFamilyId={fundFamilyId}
                    fundFamilyEventId={fundFamilyEventId}
                    message={fundFamilyEventData.out_of_date_reason}
                    onDiscard={afterDelete}
                  />
                )}
              <Text textStyle="small" color="black" marginBottom={2}>
                {t(
                  "commitment:cancelCorrect.editClass.descriptionWithNewClass",
                  {
                    investor_entity_name:
                      commitmentInformation.owning_entity_legal_name,
                    amount: formatMoney(
                      commitmentInformation.commitment_amount,
                    ),
                    old_class_name: commitmentInformation.investor_class_name,
                    new_class_name:
                      draftCancelCorrectEvent?.new_investor_class_name ?? "",
                    effective_date: formatIsoDateToDisplay(
                      draftCancelCorrectEvent?.effective_date ?? "",
                    ),
                  },
                )}
              </Text>
              <PageWizardRoutes context={pageProps} />
            </>
          )}
        </PageBody>
      </PageContent>
    </PageWizardProvider>
  );
};

const CommitmentCancelCorrectHeader: React.FC<{
  afterDelete: () => void;
}> = ({ afterDelete }) => {
  const { currentPage } = usePageWizard();
  const { t } = useTranslation(["commitment", "common"]);

  const { mutateAsync: deleteFundFamilyEvent } = useFundFamilyEventDelete({
    mutation: {
      onSuccess: () => {
        afterDelete();
      },
    },
  });

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const confirmDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog,
    {
      title: t("commitment:cancelCorrect.deleteModal.title"),
      description: t("commitment:cancelCorrect.deleteModal.description"),
      successMessage: t("commitment:cancelCorrect.deleteModal.successMessage"),
      performDelete: () =>
        deleteFundFamilyEvent({
          fundFamilyId,
          fundFamilyEventId,
        }),
    },
  );

  return (
    <PageHeader
      size="lg"
      title={currentPage!.title}
      workflowName={t("commitment:cancelCorrect.title")}
      actions={
        <Button colorScheme="destructive" onClick={confirmDelete}>
          {t("common:delete")}
        </Button>
      }
    />
  );
};
